<script setup>
import { ref, onMounted, computed, watch } from 'vue'

const projects = ref([])
const productsInfo = ref({}) // เก็บข้อมูลสินค้าสำหรับทำเรื่องอ้างอิง
const loading = ref(true)

const fetchProjects = async () => {
  loading.value = true
  try {
    const res = await fetch('/api/projects/published')
    const data = await res.json()
    if (data.success) {
      projects.value = data.data
      
      // ดึงข้อมูลสินค้าที่ผูกไว้กับผลงาน
      const productIds = new Set(data.data.filter(p => p.product_id).map(p => p.product_id))
      if (productIds.size > 0) {
        // Fetch products to map them
        const prodRes = await fetch('/api/products?is_active=1')
        const prodData = await prodRes.json()
        if (prodData.success) {
          prodData.data.forEach(prod => {
            productsInfo.value[prod.id] = prod
          })
        }
      }
    }
  } catch (err) {
    console.error('Error fetching projects:', err)
  } finally {
    loading.value = false
  }
}

// Search and Pagination
const searchQuery = ref('')
const currentPage = ref(1)
const itemsPerPage = ref(9)

const filteredProjects = computed(() => {
  let result = projects.value
  
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    result = result.filter(p => 
      (p.title && p.title.toLowerCase().includes(q)) ||
      (p.location && p.location.toLowerCase().includes(q)) ||
      (p.description && p.description.toLowerCase().includes(q))
    )
  }
  
  return result
})

const totalPages = computed(() => {
  return Math.ceil(filteredProjects.value.length / itemsPerPage.value) || 1
})

const paginatedProjects = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredProjects.value.slice(start, end)
})

watch(searchQuery, () => {
  currentPage.value = 1
})

onMounted(() => {
  fetchProjects()
})

const getImageUrl = (path) => {
  if (!path) return 'https://images.unsplash.com/photo-1545042746-831ebafafae5?q=80&w=1000'
  if (path.startsWith('http')) return path
  return `${import.meta.env.VITE_API_URL || ''}${path}`
}

const getProductLink = (prod) => {
  return prod.slug ? `/product/${prod.slug}` : `/product/${prod.id}`
}
</script>

<template>
  <div class="bg-[#f8f9fa] dark:bg-[#080c14] min-h-screen pt-24 md:pt-32 pb-10 md:pb-24 transition-colors relative overflow-hidden">
    <!-- Ambient Decor (Animated Background) -->
    <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+CgkJPGcgc3Ryb2tlPSIjMTA4YjY1IiBzdHJva2Utd2lkdGg9IjEiIG9wYWNpdHk9IjAuMDUiPgoJCQk8cGF0aCBkPSJNMCAwaDQwdjQwaC00MHoiIGZpbGw9Im5vbmUiLz4KCQk8L2c+Cjwvc3ZnPg==')] [mask-image:linear-gradient(to_bottom,white_10%,transparent_80%)] dark:[mask-image:linear-gradient(to_bottom,rgba(255,255,255,0.05)_10%,transparent_80%)] z-0"></div>
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
      <div class="absolute -top-[10%] left-[0%] w-[50vw] max-w-[500px] h-[50vw] max-h-[500px] bg-emerald-500/10 dark:bg-emerald-500/5 blur-[120px] rounded-full animate-[spin_30s_linear_infinite] origin-center mix-blend-multiply dark:mix-blend-lighten"></div>
      <div class="absolute top-[20%] right-[5%] w-[40vw] max-w-[400px] h-[40vw] max-h-[400px] bg-teal-500/10 dark:bg-teal-500/5 blur-[100px] rounded-full animate-[pulse_10s_ease-in-out_infinite] mix-blend-multiply dark:mix-blend-lighten"></div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      
      <!-- Premium Text Header -->
      <div class="text-center max-w-4xl mx-auto mb-10 md:mb-16 mt-8 md:mt-12">
        <div class="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/60 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 backdrop-blur-md mb-8 shadow-sm hover:-translate-y-0.5 transition-all duration-300">
          <span class="w-2 h-2 rounded-full bg-emerald-500 animate-[pulse_2s_ease-in-out_infinite]"></span>
          <span class="text-xs font-black text-slate-500 dark:text-slate-300 tracking-widest uppercase">Portfolio & Showcase</span>
        </div>
        <h1 class="text-5xl md:text-7xl lg:text-[5.5rem] font-black text-gray-900 dark:text-white tracking-tight leading-[1.1] mb-8">
          แฟ้มผลงาน<br class="md:hidden" />
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400">ความภูมิใจของเรา</span>
        </h1>
        <p class="text-base md:text-lg text-gray-500 dark:text-gray-400 font-medium leading-relaxed max-w-2xl mx-auto">
          สัมผัสงานติดตั้งจริงที่ผ่านการคัดสรรวัสดุพรีเมียม ตอบโจทย์ทุกการจัดสรรพื้นที่ทั้งบ้านเดี่ยว สวนหย่อม และโรงเรียนเพาะปลูกอย่างมีสไตล์
        </p>
      </div>

      <!-- Tools: Search Bar -->
      <div class="max-w-2xl mx-auto mb-12 md:mb-16 relative group">
          <!-- Glowing Drop Shadow -->
          <div class="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-3xl blur-md opacity-20 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none"></div>
          
          <div class="relative bg-white/80 dark:bg-[#111827]/80 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-3xl shadow-lg flex items-center p-2 focus-within:ring-2 focus-within:ring-emerald-500/50 transition-all duration-300">
            <div class="pl-4 pr-2 flex items-center justify-center text-gray-400 group-focus-within:text-emerald-500 transition-colors">
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            </div>
            <input v-model="searchQuery" type="text" class="w-full bg-transparent border-none text-gray-900 dark:text-white text-base md:text-lg rounded-2xl focus:ring-0 px-2 py-4 placeholder-gray-400 dark:placeholder-gray-500 font-medium outline-none" placeholder="ค้นหาผลงานจากชื่อ หรือสถานที่ติดตั้ง..."/>
            <!-- Clear Search Action -->
            <button v-if="searchQuery" @click="searchQuery = ''" class="mr-2 p-1 text-slate-400 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors rounded-full hover:bg-slate-100 dark:hover:bg-white/5">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
            <div class="pr-2 hidden sm:block">
               <span class="text-[10px] font-bold text-gray-400 bg-gray-100 dark:bg-white/5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 uppercase tracking-wider">Search</span>
            </div>
          </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-32">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mb-4"></div>
        <p class="text-gray-500 dark:text-gray-400 font-bold text-sm tracking-wide">กำลังโหลดผลงานความประทับใจ...</p>
      </div>
      
      <!-- Empty State -->
      <div v-else-if="filteredProjects.length === 0" class="text-center py-20 bg-white dark:bg-[#0f172a] rounded-[2.5rem] border border-dashed border-gray-200 dark:border-white/10 shadow-sm">
        <div class="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100 dark:border-slate-700/50">
            <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
        </div>
        <h3 class="text-lg font-black text-gray-900 dark:text-white mb-2">ไม่พบผลงานที่ค้นหา</h3>
        <p class="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto text-sm">ลองพิมพ์คำค้นหาชื่อโครงการ หรือสถานที่ติดตั้งอื่น</p>
        <button @click="searchQuery = ''" class="px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-extrabold rounded-xl hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors shadow-sm text-xs">
            ดูผลงานทั้งหมด
        </button>
      </div>

      <!-- Modern Premium Card Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
        <div v-for="project in paginatedProjects" :key="project.id" 
             class="group flex flex-col bg-white dark:bg-[#0f172a] rounded-[2.5rem] overflow-hidden shadow-sm hover:shadow-xl dark:shadow-none dark:border dark:border-white/5 transition-all duration-500 hover:-translate-y-2 relative">
             
           <!-- Image Section (Fixed Aspect Ratio) -->
          <router-link :to="`/projects/${project.slug || project.id}`" class="relative w-full aspect-[4/3] overflow-hidden bg-gray-100 dark:bg-gray-800 block">
             <img :src="getImageUrl(project.cover_image)" :alt="project.title" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105">

             <!-- Gradient Overlay for aesthetics -->
             <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
             <!-- Shiny reflection sweep effect -->
             <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.2s_ease-out_1] pointer-events-none"></div>
          </router-link>
          
          <!-- Content Section -->
          <div class="p-6 sm:p-8 flex flex-col flex-1">
             <!-- Reference Product Badge in body -->
             <div v-if="project.product_name" class="mb-3.5 flex items-center gap-1.5 text-[11px] font-bold text-slate-500 dark:text-slate-400">
               <span class="px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/10">สินค้าอ้างอิง</span>
               <span class="truncate max-w-[150px] font-medium">{{ project.product_name }}</span>
             </div>

             <div class="mb-3">
               <router-link :to="`/projects/${project.slug || project.id}`" class="block">
                   <h3 class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white leading-tight hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors line-clamp-2">{{ project.title }}</h3>
               </router-link>
             </div>
             
             <p class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-relaxed mb-6 line-clamp-2">
              {{ project.description || 'คลิกเพื่ออ่านบทความการติดตั้งและรายละเอียดความพึงพอใจของผลงานชิ้นนี้' }}
             </p>
             
             <div class="mt-auto pt-4.5 flex flex-wrap gap-y-2 justify-between items-center border-t border-slate-100 dark:border-white/5">
                <div class="flex items-center gap-1.5 text-xs text-slate-400 font-bold">
                  <svg v-if="project.location" class="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                  <span>{{ project.location || 'สถานที่: ไม่ระบุ' }}</span>
                </div>
                
                <router-link :to="`/projects/${project.slug || project.id}`" class="inline-flex items-center text-xs font-black text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors gap-1 group/btn">
                  อ่านรายละเอียด
                  <svg class="w-4 h-4 transform group-hover/btn:translate-x-1.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                </router-link>
             </div>
          </div>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div v-if="!loading && totalPages > 1" class="flex justify-center items-center gap-2 mt-16">
          <button @click="currentPage--" :disabled="currentPage === 1" class="p-2.5 rounded-xl bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"></path></svg>
          </button>
          
          <div class="flex items-center gap-1.5 md:gap-2">
              <template v-for="page in totalPages" :key="page">
                  <button v-if="page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1" 
                          @click="currentPage = page" 
                          :class="['w-10 h-10 md:w-11 md:h-11 rounded-xl text-sm md:text-base font-black shadow-sm transition-all', currentPage === page ? 'bg-gradient-to-br from-emerald-600 to-teal-500 text-white border-none shadow-emerald-500/30' : 'bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5']">
                      {{ page }}
                  </button>
                  <span v-else-if="page === 2 && currentPage > 3 || page === totalPages - 1 && currentPage < totalPages - 2" class="text-slate-400 dark:text-slate-500 px-1 font-bold">...</span>
              </template>
          </div>

          <button @click="currentPage++" :disabled="currentPage === totalPages" class="p-2.5 rounded-xl bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"></path></svg>
          </button>
      </div>

    </div>
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
